import React, { useState, useEffect } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import { RefreshCw } from 'lucide-react';
import { s, API_URL, FULL_BLEED_PAGES } from './styles/shared';

import AdminPage       from './pages/AdminPage';
import RoadmapPage     from './pages/RoadmapPage';
import DashboardPage   from './pages/DashboardPage';
import { useAuth }       from './hooks/useAuth';
import { useMarketData } from './hooks/useMarketData';
import { useAnalysis }   from './hooks/useAnalysis';

import Header         from './components/Header';
import Footer         from './components/Footer';
import HeroSection    from './components/HeroSection';
import AnalysisResult   from './components/AnalysisResult';
import AnalysisPreview from './components/AnalysisPreview';
import StockList      from './components/StockList';
import AuthPage              from './pages/AuthPage';
import RequestInviteModal   from './components/RequestInviteModal';

import ProfilePage       from './pages/ProfilePage';
import WaitlistPage      from './pages/WaitlistPage';
import VerifyEmailPage   from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { PrivacyPage, TermsPage, AboutPage, ContactPage } from './pages/legal/LegalPages';
import StockPage from './pages/StockPage';
import CalendarPage from './pages/CalendarPage';
import NewsPage from './pages/NewsPage';
import CalculatorPage from './pages/CalculatorPage';
import SharePage from './pages/SharePage';
import TradingSignalsPage from './pages/TradingSignalsPage';
import MarketPage from './pages/MarketPage';
import ScreenerPage from './pages/ScreenerPage';
// S27H: full-viewport advanced chart page — /chart/:ticker
import ChartPage from './pages/ChartPage';
// SEC-FILINGS-PAGE: standalone /filings route
import FilingsPage from './pages/FilingsPage';
// SCREENER-FINANCIALS: standalone /financials route
import FinancialsPage from './pages/FinancialsPage';
// PHASE 4.3 (Congressional Review System) — standalone /review-status route,
// NOT an AdminPage.jsx tab. Deliberately separate from isAdmin — see
// ReviewStatusPage.jsx's own header for why. Gated at render below on
// `user` only (same minimal gate AdminPage.jsx itself uses); the page's own
// GET /api/review-status call does the real access check (401/403).
// UPDATED (nav-visibility session): hasReviewConsoleAccess IS now part of
// roleInfo (admin.js's /my-role extended to include it) — used by Header.jsx
// to decide whether to SHOW the nav entry at all, separate from whether the
// page itself grants access. The comment above previously said this flag
// wasn't part of roleInfo; that's no longer accurate, corrected here rather
// than left stale.
import ReviewStatusPage from './pages/ReviewStatusPage';
// Congressional Trade Tracker — Member Page (design doc). Tab 1 only this pass.
import CongressPage from './pages/CongressPage';
// Congressional Trade Tracker — Member Detail Subpage (redesign design doc
// Section 4). Routed page, not a modal — see that file's own header for why.
import CongressMemberPage from './pages/CongressMemberPage';

export default function App() {
  const [page,          setPage]          = useState('home');
  const [pendingAnalysis,  setPendingAnalysis]  = useState(false);
  const [autoRunAnalysis,  setAutoRunAnalysis]  = useState(false);
  const [prevPage,      setPrevPage]      = useState('home');
  const [ticker,        setTicker]        = useState('');
  const [showAuth,        setShowAuth]        = useState(false);
  const [showInvite,      setShowInvite]      = useState(false);
  const [stockTicker,   setStockTicker]   = useState('');
  // S27H: ticker for full-viewport chart page — separate from stockTicker so StockPage state is unaffected
  const [chartTicker,   setChartTicker]   = useState('');
  // Congress Member Detail Subpage (redesign design doc Section 4) — separate
  // state pair, not reusing stockTicker/chartTicker, since bioguideId+chamber
  // is a different composite key shape than a plain ticker string.
  const [congressMemberBioguideId, setCongressMemberBioguideId] = useState('');
  const [congressMemberChamber,    setCongressMemberChamber]    = useState('');
  const [authMode,      setAuthMode]      = useState('login');
  const [savedFormData, setSavedFormData] = useState(null);
  const [shareId,       setShareId]       = useState('');
  const [pendingAnalysisResult, setPendingAnalysisResult] = useState(null);

  const { user, token, loginSuccess, logout, setUser } = useAuth();

  // ── URL routing ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleRoute = () => {
      const path = window.location.pathname;
      const m = path.match(/^\/stocks\/([A-Z0-9.]{1,6})$/i);
      if (m) { setStockTicker(m[1].toUpperCase()); setPage('stock'); return; }
      // S27H: /chart/:ticker — full-viewport advanced chart (handles query params in ChartPage)
      const chartMatch = path.match(/^\/chart\/([A-Z0-9.]{1,6})$/i);
      if (chartMatch) { setChartTicker(chartMatch[1].toUpperCase()); setPage('chart'); return; }
      if (path === '/market')     { setPage('market');     return; }
      if (path === '/dashboard')  { setPage('dashboard');  return; }
      if (path === '/calendar')   { setPage('calendar');   return; }
      if (path === '/filings')   { setPage('filings');   return; }
      if (path === '/financials') { setPage('financials'); return; }
      if (path === '/news')       { setPage('news');       return; }
      if (path === '/calculator') { setPage('calculator'); return; }
      if (path === '/roadmap')    { setPage('roadmap');    return; }
      if (path === '/admin')      { setPage('admin');      return; }
      // PHASE 4.3 — Congressional Review System, Pulze-side read-only status
      // mirror. Same flat-path pattern as /admin above.
      if (path === '/review-status') { setPage('reviewStatus'); return; }
      // Congress Member Detail Subpage — redesign design doc Section 4.1's
      // recommended route (/congress/member/:bioguideId?chamber=). Matched
      // BEFORE the exact '/congress' check below since this is a distinct,
      // longer path — order doesn't strictly matter here (regex vs exact
      // equality can't collide), kept in this order for readability only.
      const congressMemberMatch = path.match(/^\/congress\/member\/([A-Za-z0-9]+)$/);
      if (congressMemberMatch) {
        const chamberParam = new URLSearchParams(window.location.search).get('chamber') || '';
        setCongressMemberBioguideId(congressMemberMatch[1]);
        setCongressMemberChamber(chamberParam);
        setPage('congressMember');
        return;
      }
      // Congressional Trade Tracker — design doc Section 2 recommends /congress for brevity.
      if (path === '/congress') { setPage('congress'); return; }
      const shareMatch = path.match(/^\/share\/([a-f0-9-]{36})$/i);
      if (shareMatch) { setPage('share'); setShareId(shareMatch[1]); return; }
      if (path === '/screener')     { setPage('screener');    return; }
      if (path === '/trading/signals') { setPage('trading'); return; }
      if (path === '/privacy')  { setPage('privacy');  return; }
      if (path === '/terms')    { setPage('terms');    return; }
      if (path === '/about')    { setPage('about');    return; }
      if (path === '/contact')  { setPage('contact');  return; }
    };
    handleRoute();
    window.addEventListener('popstate', handleRoute);
    return () => window.removeEventListener('popstate', handleRoute);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open register tab if ?register=1 in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('register') === '1') {
      setAuthMode('register');
      setShowAuth(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // FIXED (July 2026) — this used to read process.env.REACT_APP_SUPER_ADMIN_EMAILS,
  // a CRA build-time variable baked into the bundle at `npm run build` time.
  // Real bug found: dev's Cloudflare Pages project never had this variable
  // (or REACT_APP_ADMIN_EMAILS) configured at all — confirmed by comparing
  // dev's and prod's actual Cloudflare "Variables and secrets" settings side
  // by side. That meant isSuperAdmin/isAdmin were silently false for
  // EVERYONE in dev, regardless of what the real backend check said —
  // completely disconnected from GET /api/admin/my-role, which already
  // existed, already worked, and already returned the correct answer when
  // tested directly via curl/Postman.
  //
  // Fixed by actually calling that endpoint instead of maintaining a second,
  // build-time, easy-to-forget-to-configure copy of the same information.
  // This is now the single source of truth — Header, DashboardPage, and
  // RoadmapPage all receive isAdmin/isSuperAdmin from this same state,
  // computed here once per token change.
  const [roleInfo, setRoleInfo] = useState({ isAdmin: false, isSuperAdmin: false, hasReviewConsoleAccess: false });

  useEffect(() => {
    if (!token) { setRoleInfo({ isAdmin: false, isSuperAdmin: false, hasReviewConsoleAccess: false }); return; }
    let cancelled = false;
    fetch(`${API_URL}/api/admin/my-role`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (!cancelled && d.success) setRoleInfo(d.data); })
      .catch(() => { if (!cancelled) setRoleInfo({ isAdmin: false, isSuperAdmin: false, hasReviewConsoleAccess: false }); });
    return () => { cancelled = true; };
  }, [token]);

  const { isAdmin, isSuperAdmin, hasReviewConsoleAccess } = roleInfo;
  const { marketData, topGainers, topLosers, mostActive, moversSource, refresh } = useMarketData(!!user);
  const { analysis, loading, error, setError, analyze, clearAnalysis } = useAnalysis(token, setUser);

  const handleAnalyze = async (overrideTicker) => {
    const t = (typeof overrideTicker === 'string' ? overrideTicker : ticker).trim();
    if (!t) { setError('Please enter a ticker symbol'); return; }
    if (!token) { handleGuestAnalyze(t); return; }
    setTicker(t);
    const ok = await analyze(t);
    if (ok) {
      if (ok.hasStockPage === true) {
        clearAnalysis();
        setPendingAnalysisResult(ok);
        navigateTo('stock', { ticker: ok.ticker || t.toUpperCase() });
      } else {
        setPage('analysis');
      }
      return;
    }
    // analyze() failed (401 stale token / 429 limit / server error)
    // Always navigate to stock page — user typed a ticker, show them the stock
    handleGuestAnalyze(t);
  };

  const handleLogout = () => {
    logout(); clearAnalysis();
    if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
    setPage('home');
  };

  const navigateTo = (pg, data) => {
    if (pg === 'stock' && data && data.ticker) {
      window.history.pushState({}, '', '/stocks/' + data.ticker);
      setStockTicker(data.ticker);
      setPage('stock');
    // S27H: navigate to full-viewport advanced chart
    // data: { ticker: 'NVDA', tf: '1M', indicators: 'bb,macd', template: 'daytrader' }
    } else if (pg === 'chart' && data && data.ticker) {
      const params = new URLSearchParams({ tf: data.tf || '1M' });
      if (data.indicators) params.set('indicators', data.indicators);
      if (data.template)   params.set('template', data.template);
      window.history.pushState({}, '', '/chart/' + data.ticker + '?' + params.toString());
      setChartTicker(data.ticker);
      setPage('chart');
    } else if (pg === 'market') {
      window.history.pushState({}, '', '/market');
      setPage('market');
    } else if (pg === 'dashboard') {
      window.history.pushState({}, '', '/dashboard');
      setPage('dashboard');
    } else if (pg === 'news') {
      window.history.pushState({}, '', '/news');
      setPage('news');
    } else if (pg === 'calendar') {
      window.history.pushState({}, '', '/calendar');
      setPage('calendar');
    } else if (pg === 'filings') {
      window.history.pushState({}, '', '/filings');
      setPage('filings');
    } else if (pg === 'financials') {
      window.history.pushState({}, '', '/financials');
      setPage('financials');
    } else if (pg === 'calculator') {
      window.history.pushState({}, '', '/calculator');
      setPage('calculator');
    } else if (pg === 'share') {
      window.history.pushState({}, '', '/share/' + (data && data.shareId ? data.shareId : ''));
      setPage('share');
    } else if (pg === 'screener') {
      window.history.pushState({}, '', '/screener');
      setPage('screener');
    } else if (pg === 'trading') {
      window.history.pushState({}, '', '/trading/signals');
      setPage('trading');
    } else if (pg === 'roadmap') {
      window.history.pushState({}, '', '/roadmap');
      setPage('roadmap');
    // PHASE 4.3 — Congressional Review System, Pulze-side read-only status mirror.
    } else if (pg === 'reviewStatus') {
      window.history.pushState({}, '', '/review-status');
      setPage('reviewStatus');
    } else if (pg === 'congress') {
      window.history.pushState({}, '', '/congress');
      setPage('congress');
    // Congress Member Detail Subpage (redesign design doc Section 4) — data:
    // { bioguideId, chamber }. chamber is REQUIRED, mirroring congress.js's own
    // required-chamber convention on this exact composite key (a bioguideId
    // alone is not unique — see that endpoint's own comment).
    } else if (pg === 'congressMember' && data && data.bioguideId && data.chamber) {
      window.history.pushState({}, '', '/congress/member/' + data.bioguideId + '?chamber=' + data.chamber);
      setCongressMemberBioguideId(data.bioguideId);
      setCongressMemberChamber(data.chamber);
      setPage('congressMember');
    } else {
      if (window.location.pathname !== '/') window.history.pushState({}, '', '/');
      setPage(pg);
    }
  };

  // Guest analyze → SEO stock page
  const handleGuestAnalyze = (t) => { navigateTo('stock', { ticker: t.toUpperCase() }); };

  const handleAuthSuccess = (newToken, newUser) => {
    loginSuccess(newToken, newUser);
    setShowAuth(false);
    setError('');
    if (pendingAnalysis && page === 'stock') {
      setPendingAnalysis(false);
      setTimeout(() => { setAutoRunAnalysis(true); }, 800);
    }
  };

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('token') && urlParams.get('page') === 'verify-email')
    return <VerifyEmailPage token={urlParams.get('token')} onLoginSuccess={handleAuthSuccess} />;
  if (urlParams.get('token') && urlParams.get('page') === 'reset-password')
    return <ResetPasswordPage token={urlParams.get('token')} onLoginSuccess={handleAuthSuccess} />;

  return (
    <ErrorBoundary>
    <div style={s.app}>
      <Header user={user} page={page} setPage={setPage} setAnalysis={clearAnalysis}
        setShowAuth={setShowAuth} setAuthMode={setAuthMode}
        setShowInvite={setShowInvite} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin}
        hasReviewConsoleAccess={hasReviewConsoleAccess} />

      {/* ── S27H: ChartPage (and any future FULL_BLEED_PAGES entry) renders OUTSIDE ── */}
      {/* s.container — full viewport width. All other pages render inside s.container. */}
      {/* main padding is 0 for full-bleed pages so content can reach edge-to-edge. */}
      <main style={{ padding: FULL_BLEED_PAGES.includes(page) ? 0 : '2rem 0', minHeight: 'calc(100vh - 200px)' }}>

        {/* ── Full-viewport advanced chart — no container wrapper ── */}
        {page === 'chart' && (
          <ChartPage
            ticker={chartTicker}
            isLoggedIn={!!user}
            token={token}
          />
        )}

        {/* ── All other pages — inside s.container as before ── */}
        {!FULL_BLEED_PAGES.includes(page) && (
          <div style={s.container}>

            {/* ── Stock page ── */}
            {page === 'stock' && (
              <StockPage
                ticker={stockTicker}
                isLoggedIn={!!user}
                token={token}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); setPendingAnalysis(true); }}
                onNavigate={navigateTo}
                onAnalyze={(t) => { setTicker(t); handleAnalyze(t); }}
                autoRunAnalysis={autoRunAnalysis}
                onAutoRunDone={() => setAutoRunAnalysis(false)}
                initialAnalysis={pendingAnalysisResult}
                onInitialAnalysisConsumed={() => setPendingAnalysisResult(null)}
              />
            )}

            {/* ── Market page ── */}
            {page === 'market' && (
              <MarketPage
                marketData={marketData}
                topGainers={topGainers}
                topLosers={topLosers}
                mostActive={mostActive}
                onNavigate={navigateTo}
                isLoggedIn={!!user}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
                onRequestInvite={() => setShowInvite(true)}
              />
            )}

            {/* ── Member Dashboard ── */}
            {page === 'dashboard' && (
              user ? (
                <DashboardPage
                  user={user}
                  token={token}
                  onNavigate={navigateTo}
                  onAnalyze={handleAnalyze}
                  marketData={marketData}
                  topGainers={topGainers}
                  topLosers={topLosers}
                  mostActive={mostActive}
                  refresh={refresh}
                  moversSource={moversSource}
                  isSuperAdmin={isSuperAdmin}
                />
              ) : (
                // Not logged in — redirect to home with auth prompt
                (() => {
                  navigateTo('home');
                  setAuthMode('login');
                  setShowAuth(true);
                  return null;
                })()
              )
            )}

            {/* ── Calculator ── */}
            {page === 'calculator' && <CalculatorPage />}

            {/* ── Screener ── */}
            {page === 'screener' && (
              <ScreenerPage
                isLoggedIn={!!user}
                token={token}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
                onNavigate={navigateTo}
              />
            )}

            {/* ── News ── */}
            {page === 'news' && (
              <NewsPage
                isLoggedIn={!!user}
                token={token}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
                onNavigate={navigateTo}
              />
            )}

            {/* ── Calendar ── */}
            {page === 'calendar' && (
              <CalendarPage
                isLoggedIn={!!user}
                token={token}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
                onNavigate={navigateTo}
              />
            )}

            {/* ── SEC Filings (standalone) — SEC-FILINGS-PAGE ── */}
            {page === 'filings' && (
              <FilingsPage
                isLoggedIn={!!user}
                token={token}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
                onNavigate={navigateTo}
              />
            )}

            {/* ── Financials (standalone) — SCREENER-FINANCIALS ── */}
            {page === 'financials' && (
              <FinancialsPage onNavigate={navigateTo} />
            )}

            {/* ── Congressional Trades — Tab 1 (Trade Feed) only this pass ── */}
            {page === 'congress' && (
              <CongressPage
                isLoggedIn={!!user}
                token={token}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
                onNavigate={navigateTo}
              />
            )}

            {/* ── Congress Member Detail Subpage — redesign design doc Section 4 ── */}
            {page === 'congressMember' && (
              <CongressMemberPage
                bioguideId={congressMemberBioguideId}
                chamber={congressMemberChamber}
                isLoggedIn={!!user}
                token={token}
                onNavigate={navigateTo}
                onRequestInvite={() => setShowInvite(true)}
                onSignIn={() => { setAuthMode('login'); setShowAuth(true); }}
              />
            )}

            {/* ── Other pages ── */}
            {page === 'pricing'  && user && <WaitlistPage currentUser={user} />}
            {page === 'waitlist' && <WaitlistPage currentUser={user} />}
            {page === 'admin'    && user && <AdminPage user={user} token={token} />}
            {/* PHASE 4.3 — Congressional Review System, Pulze-side read-only
                status mirror. Deliberately NOT gated on isAdmin here — that's
                the entire point (see ReviewStatusPage.jsx's own header).
                `user` is the same minimal gate every other member-only page
                below already uses (must be logged in at all); the real
                reviewConsoleAccess check happens server-side in the page's
                own GET /api/review-status call. */}
            {page === 'reviewStatus' && user && <ReviewStatusPage user={user} token={token} />}
            {page === 'roadmap'  && <RoadmapPage user={user} token={token} navigateTo={navigateTo} isSuperAdmin={isSuperAdmin} />}
            {page === 'share'    && <SharePage shareId={shareId} navigateTo={navigateTo} />}
            {page === 'trading'  && <TradingSignalsPage token={token} navigateTo={navigateTo} />}
            {page === 'profile'  && user && <ProfilePage user={user} token={token} onLogout={handleLogout} onPasswordChanged={() => setError('✅ Password changed!')} onAccountDeleted={() => { handleLogout(); }} />}
            {page === 'privacy'  && <PrivacyPage  setPage={setPage} prevPage={prevPage} setPrevPage={setPrevPage} setShowAuth={setShowAuth} setAuthMode={setAuthMode} setShowInvite={setShowInvite} />}
            {page === 'terms'    && <TermsPage    setPage={setPage} prevPage={prevPage} setPrevPage={setPrevPage} setShowAuth={setShowAuth} setAuthMode={setAuthMode} setShowInvite={setShowInvite} />}
            {page === 'about'    && <AboutPage    setPage={setPage} prevPage={prevPage} setPrevPage={setPrevPage} setShowAuth={setShowAuth} setAuthMode={setAuthMode} setShowInvite={setShowInvite} />}
            {page === 'contact'  && <ContactPage  setPage={setPage} prevPage={prevPage} setPrevPage={setPrevPage} setShowAuth={setShowAuth} setAuthMode={setAuthMode} setShowInvite={setShowInvite} />}

            {/* ── Home + Analysis ── */}
            {(page === 'home' || page === 'analysis') && (
              <>
                {/* Home: clean hero — no watchlist. Members see dashboard CTA inside HeroSection. */}
                {page === 'home' && (
                  <HeroSection
                    ticker={ticker} setTicker={setTicker}
                    onAnalyze={handleAnalyze} loading={loading} error={error}
                    user={user} setShowAuth={setShowAuth} setAuthMode={setAuthMode}
                    setPage={setPage} setShowInvite={setShowInvite}
                    marketData={marketData} onNavigate={navigateTo}
                  />
                )}

                {/* Analysis input bar */}
                {page === 'analysis' && (
                  <section style={{ marginBottom: '2rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '600px', margin: '0 auto' }}>
                      <input type="text" value={ticker}
                        onChange={e => setTicker(e.target.value.toUpperCase())}
                        onKeyPress={e => e.key === 'Enter' && handleAnalyze()}
                        placeholder="Analyze another ticker…"
                        style={{ flex: '1 1 280px', padding: '0.85rem 1.25rem', background: 'rgba(15,30,60,0.6)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '10px', color: '#f8fafc', fontSize: '0.95rem', outline: 'none' }} />
                      <button onClick={() => handleAnalyze()} disabled={loading}
                        style={{ padding: '0.85rem 1.5rem', background: 'linear-gradient(135deg,#00ff88,#00d4ff)', border: 'none', borderRadius: '10px', color: '#050d1a', fontWeight: 800, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                        {loading ? '⏳' : '🔮 Analyze'}
                      </button>
                    </div>
                  </section>
                )}

                {/* Analysis result */}
                {page === 'analysis' && analysis && (
                  <section style={{ marginBottom: '2.5rem' }}>
                    {analysis.isGuest
                      ? <AnalysisPreview analysis={analysis} setShowAuth={setShowAuth} setAuthMode={setAuthMode} setShowInvite={setShowInvite} />
                      : <AnalysisResult  analysis={analysis} user={user} token={token} navigateTo={navigateTo} setShowAuth={setShowAuth} setAuthMode={setAuthMode} setPage={setPage} setShowInvite={setShowInvite} />}
                    <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                      <button onClick={() => { setPage('home'); clearAnalysis(); setTicker(''); }}
                        style={{ padding: '0.8rem 1.8rem', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: '8px', color: '#00d4ff', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                        ← Back to Market
                      </button>
                    </div>
                  </section>
                )}

                {/* Movers on home page — compact, below hero */}
                {page === 'home' && (topGainers.length > 0 || topLosers.length > 0 || mostActive.length > 0) && (
                  <section>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <p style={{ fontSize: '0.75rem', color: '#64748b', margin: 0 }}>
                        {user
                          ? <span style={{ color: moversSource?.startsWith('✅') ? '#00ff88' : '#ffaa00' }}>{moversSource}</span>
                          : <span style={{ color: '#475569' }}>⏱ Market data delayed 15 min</span>}
                      </p>
                      <button onClick={refresh}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.9rem', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '6px', color: '#00d4ff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                        <RefreshCw size={13} /> Refresh
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '1.25rem' }}>
                      <StockList title="🔥 Top Gainers" stocks={topGainers} type="gainer" onSelectTicker={t => { setTicker(t); setPage('home'); }} />
                      <StockList title="📉 Top Losers"  stocks={topLosers}  type="loser"  onSelectTicker={t => { setTicker(t); setPage('home'); }} />
                      <StockList title="📊 Most Active" stocks={mostActive} type="active" onSelectTicker={t => { setTicker(t); setPage('home'); }} />
                    </div>
                    <p style={{ fontSize: '0.7rem', color: '#475569', textAlign: 'center', marginTop: '0.75rem' }}>💡 Click any stock to analyze it</p>
                  </section>
                )}
              </>
            )}

          </div>
        )}{/* end !FULL_BLEED_PAGES.includes(page) container */}

      </main>

      <Footer setPage={setPage} setShowInvite={setShowInvite} />

      {showInvite && <RequestInviteModal onClose={() => setShowInvite(false)} />}

      {showAuth && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
          <AuthPage
            initialMode={authMode}
            onSuccess={handleAuthSuccess}
            savedFormData={savedFormData}
            onFormSave={(formData) => setSavedFormData(formData)}
            onNavigate={(pg, formData) => { setShowAuth(false); setPrevPage('auth'); setSavedFormData(formData); setPage(pg); }}
            onClose={() => { setShowAuth(false); setSavedFormData(null); setError(''); }}
          />
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
}

